import { useEffect, useRef, useState } from 'react';
import { Loader2, ShieldCheck } from 'lucide-react';
import AccessDenied from '../AccessDenied/AccessDenied';
import { initials } from './CommentCard';

const cx = (...v: Array<string | false | null | undefined>) => v.filter(Boolean).join(' ');

/** Copy do design (frame `tcgeJ`) — é o que informa a pré-moderação ao aluno. */
export const MODERATION_NOTE = 'Os comentários são revisados por um moderador antes de aparecer.';

export interface CommentComposerProps {
  onSubmit: (body: string) => Promise<unknown> | void;
  /**
   * Assinatura ativa (`PAID`). Sem ela a lista continua visível e só o
   * composer é trocado pela negativa padrão do produto.
   */
  canParticipate?: boolean;
  /** Nome do usuário logado — vira as iniciais do avatar. */
  authorName?: string;
  placeholder?: string;
  submitLabel?: string;
  /** Some para `administrador`/`master`: o comentário deles publica direto. */
  showModerationNote?: boolean;
  /** Variante das respostas e da edição: sem avatar, campo menor. */
  compact?: boolean;
  initialValue?: string;
  onCancel?: () => void;
  autoFocus?: boolean;
  /** Navegação SPA para os links da negativa; sem isso são `<a href>`. */
  onNavigate?: (to: string) => void;
  className?: string;
}

/**
 * Campo de novo comentário — e, na variante `compact`, de resposta e de edição.
 *
 * Três regras vêm direto da spec: "Publicar" fica desabilitado enquanto o texto
 * está vazio, o aviso de pré-moderação é sempre visível para o aluno, e quem
 * não tem assinatura vê uma negativa no lugar do campo — na família `warn`,
 * nunca em vermelho, porque ser barrado não é falha do usuário (§7).
 */
export default function CommentComposer({
  onSubmit,
  canParticipate = false,
  authorName,
  placeholder = 'Compartilhe uma dica ou tire uma dúvida sobre esta questão…',
  submitLabel = 'Publicar',
  showModerationNote = true,
  compact = false,
  initialValue = '',
  onCancel,
  autoFocus = false,
  onNavigate,
  className,
}: CommentComposerProps) {
  const [value, setValue] = useState(initialValue);
  const [sending, setSending] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (autoFocus) textareaRef.current?.focus();
  }, [autoFocus]);

  if (!canParticipate) {
    return (
      <div data-testid="composer-blocked" className={className}>
        <AccessDenied kind="subscription" variant="panel" onNavigate={onNavigate} />
      </div>
    );
  }

  const empty = !value.trim();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (empty || sending) return;
    setSending(true);
    try {
      await onSubmit(value.trim());
      setValue('');
    } catch {
      // A falha da API já virou toast no interceptor global
      // (`app/auth/httpConfig.ts`). O que cabe aqui é não engolir o texto
      // digitado — ele fica no campo para o usuário tentar de novo — e não
      // deixar a rejeição escapar do handler do `submit`: publicar não pode
      // terminar num "Uncaught (in promise)".
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={cx('flex gap-3', className)}>
      {!compact && (
        <span
          aria-hidden
          className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-surface-hover text-xs font-bold text-ink-secondary"
        >
          {initials(authorName ?? 'Você')}
        </span>
      )}

      <div className="flex min-w-0 flex-1 flex-col gap-2 rounded-input border border-edge bg-surface px-3 py-2.5 focus-within:border-edge-strong">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(event) => setValue(event.target.value)}
          placeholder={placeholder}
          aria-label={submitLabel === 'Publicar' ? 'Escreva um comentário' : submitLabel}
          rows={compact ? 2 : 3}
          className="w-full resize-y border-0 bg-transparent p-0 text-sm leading-relaxed text-ink outline-none placeholder:text-ink-muted"
        />

        <div className="flex flex-wrap items-center justify-between gap-2">
          {showModerationNote ? (
            <p className="m-0 inline-flex items-center gap-1.5 text-xs text-ink-muted">
              <ShieldCheck size={13} aria-hidden className="shrink-0" />
              {MODERATION_NOTE}
            </p>
          ) : (
            <span />
          )}

          <div className="flex items-center gap-2">
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="rounded-input px-3 py-1.5 text-xs font-semibold text-ink-secondary transition-colors hover:bg-surface-hover hover:text-ink"
              >
                Cancelar
              </button>
            )}
            <button
              type="submit"
              disabled={empty || sending}
              className={cx(
                'inline-flex items-center gap-2 rounded-input px-4 py-1.5 text-xs font-semibold',
                'bg-brand text-brand-contrast transition-[filter] hover:brightness-110',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-select/60',
                'focus-visible:ring-offset-2 focus-visible:ring-offset-surface',
                'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:brightness-100',
              )}
            >
              {sending && <Loader2 size={13} className="animate-spin" aria-hidden />}
              {submitLabel}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
}
